import React, { createContext, useState, useEffect } from 'react';
import axiosInstance from '../api/axiosInstance';
import { User } from '../types';

export interface AuthContextType {
  user: User | null;
  userRoles: string[];
  loading: boolean;
  login: (username: string, password: string) => Promise<void>;
  logout: () => void;
  isAuthenticated: boolean;
  refetch: () => Promise<void>;
}

export const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [userRoles, setUserRoles] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);

  const checkAuth = async () => {
    setLoading(true);
    try {
      const response = await axiosInstance.get('/api/auth/me');
      const userData = response.data;
      setUser(userData);
      
      let roles: string[] = [];
      if (userData && userData.roles && Array.isArray(userData.roles)) {
        roles = userData.roles;
      } else if (userData && userData.role) {
        roles = [userData.role];
      }

      try {
        const rolesRes = await axiosInstance.get('/api/roles/me');
        if (Array.isArray(rolesRes.data)) {
          const fetchedRoles = rolesRes.data.map((role: any) => role.name || role);
          roles = Array.from(new Set([...roles, ...fetchedRoles]));
        }
      } catch (roleErr) {
        console.warn('/api/roles/me 조회 실패, me 응답의 roles 사용:', roleErr);
      }

      setUserRoles(roles);
    } catch (error) {
      setUser(null);
      setUserRoles([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    checkAuth();
  }, []);

  const login = async (username: string, password: string) => {
    try {
      const response = await axiosInstance.post('/api/auth/login', { username, password });
      const userData = response.data.user;
      setUser(userData);
      
      let roles: string[] = [];
      if (userData && userData.roles && Array.isArray(userData.roles)) {
        roles = userData.roles;
      }

      try {
        const rolesRes = await axiosInstance.get('/api/roles/me');
        if (Array.isArray(rolesRes.data)) {
          const fetchedRoles = rolesRes.data.map((role: any) => role.name || role);
          roles = Array.from(new Set([...roles, ...fetchedRoles]));
        }
      } catch (roleErr) {
        console.warn('/api/roles/me 조회 실패:', roleErr);
      }

      setUserRoles(roles);
    } catch (error) {
      throw error;
    }
  };

  const logout = () => {
    setUser(null);
    setUserRoles([]);
  };

  return (
    <AuthContext.Provider value={{ 
      user, 
      userRoles, 
      loading, 
      login, 
      logout, 
      isAuthenticated: !!user,
      refetch: checkAuth 
    }}>
      {children}
    </AuthContext.Provider>
  );
}; 